import Bar from 'widget/components/Progress/primitives/Bar';
import Bars from 'widget/components/Progress/primitives/Bars';
import useMount from 'hooks/useMount';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { generatePerlinNoise } from 'perlin-noise';
import { throttle } from 'lodash-es';

type Props = {
  barWidth?: number;
  duration: number;
  minBarHeight?: number;
  position: number;
};

function Waveform({
  barWidth = 3,
  duration,
  minBarHeight = 0.3,
  position,
}: Props) {
  const [containerDimensions, setContainerDimensions] = useState<{
    height: number;
    width: number;
  } | null>(null);

  const barValues = useMemo<Array<number>>(() => {
    if (!containerDimensions) return [];
    const barCount = Math.floor(containerDimensions.width / barWidth / 2);
    const noise = generatePerlinNoise(barCount, 1, { persistence: 1 });

    return noise.map((datum: number) => {
      if (datum < minBarHeight)
        return minBarHeight * containerDimensions.height;
      return Math.floor(datum * containerDimensions.height);
    });
  }, [barWidth, containerDimensions, minBarHeight]);

  const barsRef = useRef<SVGSVGElement | null>(null);

  const setDimensions = useCallback<() => void>(() => {
    if (barsRef.current === null) return;
    const dimensions = barsRef.current.getBoundingClientRect();
    setContainerDimensions(dimensions);
  }, [setContainerDimensions]);

  useMount(() => {
    const throttledUpdateDimensions = throttle(setDimensions, 100);
    window.addEventListener('resize', throttledUpdateDimensions);
    return () =>
      window.removeEventListener('resize', throttledUpdateDimensions);
  });

  const measure = useCallback<(instance: SVGSVGElement | null) => void>(
    node => {
      if (node === null) return;
      barsRef.current = node;
      setDimensions();
    },
    [setDimensions],
  );

  return (
    <Bars ref={measure}>
      {barValues.map((barValue, i) => {
        if (!containerDimensions) return null;

        const barRadius = Math.floor(barWidth / 2);
        const offsetLeft = i * barWidth * 2;
        const offsetTop = containerDimensions.height - barValue + barRadius;
        const percentOfTimeElapsed = duration > 0 ? position / duration : 0;
        const percentOfDistanceFromLeftSideOfSVGContainer =
          offsetLeft / containerDimensions.width;
        const hidden =
          percentOfTimeElapsed <= percentOfDistanceFromLeftSideOfSVGContainer;

        return (
          <Fragment key={offsetLeft}>
            <Bar
              height={barValue}
              index={i}
              rx={barRadius}
              width={barWidth}
              x={offsetLeft}
              y={offsetTop}
            />
            <Bar
              height={barValue}
              hidden={hidden}
              rx={barRadius}
              width={barWidth}
              x={offsetLeft}
              y={offsetTop}
            />
          </Fragment>
        );
      })}
    </Bars>
  );
}

export default Waveform;
